import { type UrlsInText, UrlsInTextType } from '@/@types/common'
import { isElementContentOverflow } from '@/utils/dom'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { getMobileHashtagURL } from '@/utils/url'
import storage from '@/utils/Storage'
import { Link as RouterLink } from '@tanstack/react-router'
import clsx from 'clsx'
import { escapeRegExp, isEmpty, isNil, last, map } from 'lodash-es'
import {
	type HTMLAttributes,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'
import reactStringReplace from 'react-string-replace'
import ClickWrapper from './ClickWrapper'
import Link from './Link'
import MentionUser from './MentionUser'
import MentionUserPlaceholder from './MentionUserPlaceholder'
import UserLoader from './Post/UserLoader'
import styles from './TextContent.module.css'

export interface TextContentProps extends HTMLAttributes<HTMLDivElement> {
	content: string
	urlsInText?: UrlsInText[]

	/**
	 * 是否可折叠
	 *
	 * @default false
	 */
	autoCollapsable?: boolean
}

export default function TextContent({
	content,
	urlsInText = [],
	autoCollapsable = false,
	...props
}: TextContentProps) {
	const [isOverflowInCollapsed, setIsOverflowInCollapsed] = useState<boolean>(false)
	const [isExpanded, setIsExpanded] = useState<boolean>(!autoCollapsable)
	const ref = useRef<HTMLDivElement | null>(null)

	const navigate = useUniversalNavigate()
	const isAnonymous = !storage.auth.token
	const renderContent = useCallback((): ReactNode => {
		const text = content
		if (isEmpty(urlsInText) || isNil(text)) {
			return text
		}

		const reg = new RegExp(
			`(${map(urlsInText, 'originalUrl')
				.map((item) => `${escapeRegExp(item)}`)
				.join('|')})`,
			'g'
		)
		return reactStringReplace(text, reg, (match, index) => {
			const urlInText = urlsInText.find((item) => item.originalUrl === match)
			if (isNil(urlInText)) return null

			const renderLink = (href: string, target?: '_blank') => (
				<Link
					key={[text, href, index].join('_')}
					href={href}
					target={target}
					onClick={(e) => {
						e.stopPropagation()
						if (target) {
							return
						}

						e.preventDefault()
						navigate(href, e)
					}}
				>
					{urlInText.title}
				</Link>
			)

			switch (urlInText.type) {
				case UrlsInTextType.LINK:
					return renderLink(urlInText.originalUrl)
				case UrlsInTextType.HASHTAG: {
					const hashtagURL = getMobileHashtagURL(urlInText.url)
					return hashtagURL ? renderLink(hashtagURL, '_blank') : match
				}
				case UrlsInTextType.MENTION: {
					const username = last(urlInText.url.split('/'))
					if (isNil(username)) {
						return null
					}
					return (
						<RouterLink
							key={[text, username, index].join('_')}
							to="/u/$username"
							params={{
								username,
							}}
							onClick={(e) => {
								e.stopPropagation()
							}}
						>
							{isAnonymous ? (
								<MentionUser
									data={{ screenName: urlInText.title }}
									className={styles.mentionUser}
								/>
							) : (
								<UserLoader
									username={username}
									placeholder={<MentionUserPlaceholder className={styles.mentionUser} />}
								>
									{(user) => (
										<MentionUser
											data={{
												avatar: user.avatarImage.thumbnailUrl ?? user.avatarImage.picUrl,
												screenName: user.screenName,
											}}
											className={styles.mentionUser}
										/>
									)}
								</UserLoader>
							)}
						</RouterLink>
					)
				}
			}
			return null
		})
	}, [content, isAnonymous, navigate, urlsInText])

	useEffect(() => {
		const contentElement = ref.current
		if (!contentElement || !autoCollapsable) {
			return
		}

		const isContentOverflow = isElementContentOverflow(contentElement)
		setIsOverflowInCollapsed(isContentOverflow)
		setIsExpanded(!isContentOverflow)
	}, [autoCollapsable])

	if (isEmpty(content)) {
		return null
	}
	return (
		<div {...props}>
			<div
				ref={ref}
				className={clsx(
					styles.content,
					!isExpanded && styles.contentCollapsed,
					autoCollapsable && !isExpanded && styles.contentEllipsis
				)}
			>
				{renderContent()}
			</div>

			{autoCollapsable && isOverflowInCollapsed && (
				<ClickWrapper
					whileTap={{
						scale: 0.98,
					}}
					className={styles.expandButton}
					onClick={(e) => {
						e.stopPropagation()
						setIsExpanded((prev) => !prev)
					}}
				>
					{isExpanded ? '收起' : '查看更多'}
				</ClickWrapper>
			)}
		</div>
	)
}
